/**
 * Readable proposal diff — v2. No deps.
 *
 * Three views behind a segmented toggle:
 *   Changes — hunked unified diff: runs of unchanged lines collapse into a
 *             "N unchanged lines" separator (click to expand), changed lines
 *             get soft red/green tints, and paired removed→added lines get
 *             word-level highlights so the reader sees WHAT changed inside
 *             a sentence, not just that the line changed.
 *   Before  — the current value, clean full text.
 *   After   — the proposed value, clean full text.
 *
 * Design goals (2026-07-24 redesign): a non-technical admin must be able to
 * review an edit without reading a wall of red/green. Line numbers were
 * dropped (internal noise); a +N/−M tally chip gives scale at a glance.
 */

import React, { useMemo, useState } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import './DiffView.css';

interface DiffViewProps {
  left: string;
  right: string;
  /** Cap the diff height; scrolls beyond. */
  maxHeight?: number;
}

type Op = 'equal' | 'removed' | 'added';
interface DiffRow {
  op: Op;
  text: string;
}

/** Word-level segment of a changed line. */
interface Seg {
  text: string;
  changed: boolean;
}

/** Display model: either a visible diff row (with optional word segments)
 *  or a collapsed run of unchanged lines. */
type DisplayItem =
  | { kind: 'row'; op: Op; text: string; segs?: Seg[] }
  | { kind: 'collapse'; rows: DiffRow[] };

/** Line-level LCS diff. O(N*M) — fine for prompts up to a few thousand lines. */
function diffLines(a: string, b: string): DiffRow[] {
  const al = a.split(/\r?\n/);
  const bl = b.split(/\r?\n/);
  const m = al.length;
  const n = bl.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      dp[i][j] = al[i - 1] === bl[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const rows: DiffRow[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (al[i - 1] === bl[j - 1]) {
      rows.unshift({ op: 'equal', text: al[i - 1] });
      i -= 1;
      j -= 1;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      rows.unshift({ op: 'removed', text: al[i - 1] });
      i -= 1;
    } else {
      rows.unshift({ op: 'added', text: bl[j - 1] });
      j -= 1;
    }
  }
  while (i > 0) {
    rows.unshift({ op: 'removed', text: al[i - 1] });
    i -= 1;
  }
  while (j > 0) {
    rows.unshift({ op: 'added', text: bl[j - 1] });
    j -= 1;
  }
  return rows;
}

/** Token-level LCS between two lines → segments with `changed` flags.
 *  Tokens keep their trailing whitespace so joining reproduces the line. */
function diffWords(a: string, b: string): { del: Seg[]; add: Seg[] } {
  const tok = (s: string): string[] => s.match(/\S+\s*|\s+/g) || [];
  const at = tok(a);
  const bt = tok(b);
  const m = at.length;
  const n = bt.length;
  // Guard pathological lines — fall back to whole-line marks.
  if (m * n > 40000) {
    return {
      del: [{ text: a, changed: true }],
      add: [{ text: b, changed: true }],
    };
  }
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      dp[i][j] = at[i - 1] === bt[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const del: Seg[] = [];
  const add: Seg[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (at[i - 1] === bt[j - 1]) {
      del.unshift({ text: at[i - 1], changed: false });
      add.unshift({ text: bt[j - 1], changed: false });
      i -= 1;
      j -= 1;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      del.unshift({ text: at[i - 1], changed: true });
      i -= 1;
    } else {
      add.unshift({ text: bt[j - 1], changed: true });
      j -= 1;
    }
  }
  while (i > 0) {
    del.unshift({ text: at[i - 1], changed: true });
    i -= 1;
  }
  while (j > 0) {
    add.unshift({ text: bt[j - 1], changed: true });
    j -= 1;
  }
  // Merge adjacent segments with the same flag so we emit few <mark>s.
  const merge = (segs: Seg[]): Seg[] => {
    const out: Seg[] = [];
    for (const s of segs) {
      const last = out[out.length - 1];
      if (last && last.changed === s.changed) last.text += s.text;
      else out.push({ ...s });
    }
    return out;
  };
  return { del: merge(del), add: merge(add) };
}

/** How many equal lines to keep visible around each change. */
const CONTEXT = 2;
/** Equal runs longer than this (beyond the context kept on each side) collapse. */
const COLLAPSE_MIN = 3;

/** Build the display model: context-limited hunks + word-paired changed lines. */
function buildDisplay(rows: DiffRow[]): DisplayItem[] {
  // 1. Word-pair consecutive removed/added runs of equal length positions.
  //    Walk the rows; on a removed-run followed by an added-run, pair
  //    removed[k] ↔ added[k] and attach word segments when the lines are
  //    similar enough to be a "modification" (vs unrelated add/remove).
  const withSegs: (DiffRow & { segs?: Seg[] })[] = [];
  let idx = 0;
  while (idx < rows.length) {
    if (rows[idx].op !== 'removed') {
      withSegs.push(rows[idx]);
      idx += 1;
      continue;
    }
    const remStart = idx;
    while (idx < rows.length && rows[idx].op === 'removed') idx += 1;
    const addStart = idx;
    while (idx < rows.length && rows[idx].op === 'added') idx += 1;
    const removed = rows.slice(remStart, addStart);
    const added = rows.slice(addStart, idx);
    const pairs = Math.min(removed.length, added.length);
    const remOut: (DiffRow & { segs?: Seg[] })[] = removed.map((r) => ({ ...r }));
    const addOut: (DiffRow & { segs?: Seg[] })[] = added.map((r) => ({ ...r }));
    for (let k = 0; k < pairs; k += 1) {
      const { del, add } = diffWords(removed[k].text, added[k].text);
      // Only treat as a modification when a meaningful share of the line
      // survived — otherwise whole-line coloring reads better.
      const kept = del.filter((s) => !s.changed).reduce((acc, s) => acc + s.text.length, 0);
      if (kept >= Math.min(12, removed[k].text.length * 0.3)) {
        remOut[k].segs = del;
        addOut[k].segs = add;
      }
    }
    withSegs.push(...remOut, ...addOut);
  }

  // 2. Collapse long equal runs, keeping CONTEXT lines around changes.
  const items: DisplayItem[] = [];
  let k = 0;
  while (k < withSegs.length) {
    const row = withSegs[k];
    if (row.op !== 'equal') {
      items.push({ kind: 'row', op: row.op, text: row.text, segs: row.segs });
      k += 1;
      continue;
    }
    // Measure the equal run.
    let end = k;
    while (end < withSegs.length && withSegs[end].op === 'equal') end += 1;
    const runLen = end - k;
    const isLeading = k === 0;
    const isTrailing = end === withSegs.length;
    const keepBefore = isLeading ? 0 : CONTEXT; // context after the PREVIOUS change
    const keepAfter = isTrailing ? 0 : CONTEXT; // context before the NEXT change
    if (runLen > keepBefore + keepAfter + COLLAPSE_MIN) {
      for (let c = 0; c < keepBefore; c += 1) {
        items.push({ kind: 'row', op: 'equal', text: withSegs[k + c].text });
      }
      items.push({
        kind: 'collapse',
        rows: withSegs.slice(k + keepBefore, end - keepAfter).map((r) => ({ op: 'equal' as Op, text: r.text })),
      });
      for (let c = end - keepAfter; c < end; c += 1) {
        items.push({ kind: 'row', op: 'equal', text: withSegs[c].text });
      }
    } else {
      for (let c = k; c < end; c += 1) {
        items.push({ kind: 'row', op: 'equal', text: withSegs[c].text });
      }
    }
    k = end;
  }
  return items;
}

type ViewMode = 'changes' | 'before' | 'after';

export const DiffView: React.FC<DiffViewProps> = ({ left, right, maxHeight = 380 }) => {
  const { t } = useLanguage();
  const leftText = left ?? '';
  const rightText = right ?? '';
  const heavy = leftText.length + rightText.length > 60000;
  const rows = useMemo<DiffRow[] | null>(
    () => (heavy ? null : diffLines(leftText, rightText)),
    [heavy, leftText, rightText],
  );
  const display = useMemo<DisplayItem[] | null>(
    () => (rows ? buildDisplay(rows) : null),
    [rows],
  );
  const tally = useMemo<{ added: number; removed: number }>(() => {
    if (!rows) return { added: 0, removed: 0 };
    let added = 0;
    let removed = 0;
    for (const r of rows) {
      if (r.op === 'added') added += 1;
      else if (r.op === 'removed') removed += 1;
    }
    return { added, removed };
  }, [rows]);

  const [view, setView] = useState<ViewMode>(heavy ? 'after' : 'changes');
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

  const noChanges = !heavy && tally.added === 0 && tally.removed === 0;
  const isNewValue = leftText.trim() === '' && rightText.trim() !== '';

  const viewBtn = (mode: ViewMode, label: string) => (
    <button
      type="button"
      className={`aoa-diff-viewbtn${view === mode ? ' aoa-diff-viewbtn--active' : ''}`}
      onClick={() => setView(mode)}
      aria-pressed={view === mode}
    >
      {label}
    </button>
  );

  return (
    <div className="aoa-diff-wrap">
      <div className="aoa-diff-bar">
        <div className="aoa-diff-views" role="tablist">
          {!heavy && viewBtn('changes', t('metaAgent.proposal.viewChanges') || 'Changes')}
          {viewBtn('before', t('metaAgent.proposal.viewBefore') || 'Before')}
          {viewBtn('after', t('metaAgent.proposal.viewAfter') || 'After')}
        </div>
        {!heavy && !noChanges && (
          <div className="aoa-diff-tally" aria-label={`${tally.added} added, ${tally.removed} removed`}>
            {tally.added > 0 && <span className="aoa-diff-tally-add">+{tally.added}</span>}
            {tally.removed > 0 && <span className="aoa-diff-tally-del">−{tally.removed}</span>}
          </div>
        )}
      </div>

      <div className="aoa-diff" style={{ maxHeight }}>
        {view === 'changes' && display && (
          <div className="aoa-diff-table" role="presentation">
            {noChanges && (
              <div className="aoa-diff-empty">
                {t('metaAgent.proposal.noChanges') || 'No text changes.'}
              </div>
            )}
            {display.map((item, i) => {
              if (item.kind === 'collapse') {
                if (expanded.has(i)) {
                  return item.rows.map((r, ri) => (
                    <div key={`${i}-${ri}`} className="aoa-diff-row aoa-diff-equal">
                      <span className="aoa-diff-marker" aria-hidden> </span>
                      <span className="aoa-diff-text" dir="auto">{r.text || ' '}</span>
                    </div>
                  ));
                }
                return (
                  <button
                    key={i}
                    type="button"
                    className="aoa-diff-collapse"
                    onClick={() => setExpanded((prev) => new Set(prev).add(i))}
                  >
                    <span className="aoa-diff-collapse-line" aria-hidden />
                    {t('metaAgent.proposal.unchangedLines', { count: item.rows.length }) ||
                      `${item.rows.length} unchanged lines`}
                    <span className="aoa-diff-collapse-line" aria-hidden />
                  </button>
                );
              }
              return (
                <div key={i} className={`aoa-diff-row aoa-diff-${item.op}`}>
                  <span className="aoa-diff-marker" aria-hidden dir="auto">
                    {item.op === 'added' ? '+' : item.op === 'removed' ? '−' : ' '}
                  </span>
                  <span className="aoa-diff-text" dir="auto">
                    {item.segs
                      ? item.segs.map((s, si) =>
                          s.changed ? (
                            <mark
                              key={si}
                              className={
                                item.op === 'added' ? 'aoa-diff-word-add' : 'aoa-diff-word-del'
                              }
                            >
                              {s.text}
                            </mark>
                          ) : (
                            <React.Fragment key={si}>{s.text}</React.Fragment>
                          ),
                        )
                      : item.text || ' '}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {view === 'before' && (
          <div className="aoa-diff-pane">
            {leftText.trim() === '' ? (
              <div className="aoa-diff-empty">
                {isNewValue
                  ? t('metaAgent.proposal.beforeEmpty') || 'This field was empty.'
                  : t('metaAgent.proposal.noChanges') || 'No text changes.'}
              </div>
            ) : (
              <pre className="aoa-diff-fallback" dir="auto">{leftText}</pre>
            )}
          </div>
        )}

        {view === 'after' && (
          <div className="aoa-diff-pane">
            <pre className="aoa-diff-fallback" dir="auto">{rightText}</pre>
          </div>
        )}
      </div>
    </div>
  );
};
